/**
 * /shared/:token — the read-only candidate report behind a share link.
 *
 * It is NOT a separate design: it renders the app's own candidate review page (users/Review.tsx)
 * inside a ReportSourceProvider carrying the share token. Every panel then reads the share-scoped
 * endpoints (GET /shared/{token}, …/details, …/observations) and every action — decisions, team
 * notes, activity log, edits, re-scoring, sharing — is hidden. A change to the review page shows
 * up here automatically.
 *
 * What a share may carry is decided server-side (sharedReport.ts): a redacted share (the default)
 * withholds the candidate's name, contact details, resume, candidate details, observations,
 * recording and transcript; quoted evidence is blanked. This shell only adds the frame — the
 * read-only bar, the banner, and the invalid / expired / revoked state.
 */
import type { ReactNode } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api.js";
import { useTheme } from "../lib/theme.js";
import { Info, Moon, ShieldCheck } from "../components/icons.js";
import { Review } from "./users/Review.js";
import { ReportSourceProvider } from "./users/reportSource.js";

export function SharedReport(): ReactNode {
  const params = useParams<{ token: string }>();
  const token = params.token ?? "";
  const { cycle } = useTheme();

  // Same query key Review uses in shared mode — one request serves the frame and the page.
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["sharedReport", token],
    queryFn: () => api.get<{ report: { redacted?: boolean } }>(`/shared/${token}`),
    enabled: token.length > 0,
  });
  const report = data?.report ?? null;
  const badLink =
    token.length === 0 ||
    (isError && error instanceof ApiError && (error.status === 404 || error.status === 410 || error.status === 403));

  return (
    <div className="cx t1-sr" style={{ maxWidth: 1240, margin: "0 auto", padding: "16px 20px" }}>
      <div className="cx-shell">
        <div className="cx-bar" style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px" }}>
          <span
            className="mark"
            style={{
              width: 26,
              height: 26,
              borderRadius: 6,
              background: "var(--frame)",
              display: "grid",
              placeItems: "center",
              color: "var(--lime)",
            }}
          >
            <ShieldCheck />
          </span>
          <b style={{ fontSize: 14.5 }}>TalentHop · Shared report</b>
          <span className="pill n" style={{ marginLeft: "auto" }}>
            Read-only
          </span>
          <button className="theme-toggle" style={{ marginLeft: 8 }} onClick={cycle} aria-label="Toggle theme" data-tip="Toggle theme" data-tip-pos="left">
            <Moon />
          </button>
        </div>

        <div className="cx-step" style={{ padding: "16px 20px" }}>
          <div className="banner info" style={{ marginBottom: 18 }}>
            <Info />
            <div>
              <b>Shared report · read-only.</b> Scores come from the content of answers, cited to the
              transcript and traced to a versioned rubric. The engine ranks — a human decides.
              {report?.redacted && " This is a redacted share: personal details, recording and transcript are withheld."}
            </div>
          </div>

          {isLoading && <div className="card pad mut sm">Loading the shared report…</div>}
          {badLink && !isLoading && (
            <div className="card pad" style={{ textAlign: "center" }}>
              <h3 style={{ marginTop: 0 }}>This share link is invalid, expired or was revoked</h3>
              <p className="mut sm" style={{ marginBottom: 0 }}>
                Report shares are time-limited and revocable by the org that created them. Ask the sender for a
                fresh link.
              </p>
            </div>
          )}
          {isError && !badLink && (
            <div className="banner crit">
              <Info />
              <span>Couldn't load the report. Please retry in a moment.</span>
            </div>
          )}

          {report && (
            <ReportSourceProvider shareToken={token}>
              <Review />
            </ReportSourceProvider>
          )}
        </div>
      </div>
    </div>
  );
}
